import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  FolderOpen,
  Settings,
  TrendingUp,
  Layers,
  Users
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectTypeMasterService from '../../services/projectTypeMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectTypeMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProjectTypes: 0,
    activeProjectTypes: 0,
    inactiveProjectTypes: 0,
    internalProjects: 0,
    externalProjects: 0,
    supportProjects: 0,
    developmentProjects: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedProjectTypeData, setSelectedProjectTypeData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [projectTypesResponse, statsResponse] = await Promise.all([
        projectTypeMasterService.getAllProjectTypeMasters(),
        projectTypeMasterService.getProjectTypeMasterStats()
      ])
      
      if (projectTypesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (projectTypesResponse.data || []).map(projectType => ({
          ...projectType,
          id: projectType._id,
          formattedDefaultBudget: projectType.defaultBudget ? 
            `${projectType.defaultCurrency} ${projectType.defaultBudget.toLocaleString()}` : 
            'Not Set',
          formattedDefaultDuration: projectType.defaultDuration ? 
            `${projectType.defaultDuration} days` : 
            'Not Set',
          formattedSupportedPricingModels: projectType.supportedPricingModels?.join(', ') || 'Not Set',
          formattedDefaultResourceRoles: projectType.defaultResourceRoles?.join(', ') || 'Not Set',
          currentVersion: `Rev ${projectType.revisionHistory?.length + 1 || 1}`
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (projectTypesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No Project Type Master records found. Create your first Project Type!')
        }
      } else {
        toast.error(projectTypesResponse.message || 'Failed to load Project Type Master records')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading Project Type Master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view Project Type Master records')
      } else {
        toast.error('Failed to load Project Type Master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      key: 'projectTypeCode',
      label: 'Project Type Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Layers size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'projectTypeName',
      label: 'Project Type Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.projectTypeDescription && (
            <div className="text-sm text-gray-500">{row.projectTypeDescription}</div>
          )}
        </div>
      )
    },
    {
      key: 'projectTypeCategory',
      label: 'Category',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          'Internal': 'bg-blue-100 text-blue-800',
          'External': 'bg-green-100 text-green-800',
          'Support': 'bg-yellow-100 text-yellow-800',
          'Development': 'bg-purple-100 text-purple-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'formattedDefaultBudget',
      label: 'Default Budget',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      key: 'projectTypeStatus',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    }
  ]

  const handleAddNew = () => {
    navigate('/projects/project-type-master/new')
  }

  const handleEdit = (row) => {
    navigate('/projects/project-type-master/edit', { 
      state: { projectTypeData: row } 
    })
  }

  const handleView = (row) => {
    navigate(`/projects/project-type-master/view/${row.id}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectTypeMasterService.deleteProjectTypeMaster(deleteItem.id)
      if (response.success) {
        toast.success('Project Type Master deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete Project Type Master')
      }
    } catch (error) {
      console.error('Error deleting Project Type Master:', error)
      toast.error('Failed to delete Project Type Master')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedProjectTypeData(row)
    setShowRevisionHistory(true)
  }


  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Masters',
      path: '/projects?tab=masters',
      onClick: () => navigate('/projects?tab=masters')
    },
    {
      label: 'Project Type Master',
      path: '/projects/project-type-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Type Master</h1>
          <p>Manage project types and categories for your organization</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FolderOpen size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProjectTypes}</div>
          <div className="metric-title">Total Project Types</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeProjectTypes}</div>
          <div className="metric-title">Active Project Types</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Layers size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.internalProjects}</div>
          <div className="metric-title">Internal Projects</div>
          <div className="metric-subtitle">Internal project types</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.externalProjects}</div>
          <div className="metric-title">External Projects</div>
          <div className="metric-subtitle">Client project types</div>
        </div>
      </div>

      {/* Project Type Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search project types..."
          emptyMessage="No project types found. Create your first project type!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Type Master"
        message={`Are you sure you want to delete "${deleteItem?.projectTypeName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedProjectTypeData}
        title="Project Type Master Revision History"
      />
    </div>
  )
}

export default ProjectTypeMaster
